import classNames from 'classnames';
import Immutable from 'immutable';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector, batch } from 'react-redux';
import AccessibleDiv from 'components/AccessibleDiv/AccessibleDiv';
import ActionIcon from 'components/ActionIcon';
import { ActionsColor } from 'components/ArtboardLayoutActions/constants';
import { useCarouselItemSlideDragSource } from 'components/CarouselItemSlide/useCarouselItemSlideDragSource';
import { IconType } from 'components/Icon/constants';
import { updateDragHotspotPosition } from 'containers/App/actions';
import { setFrozenEntityIdAction } from 'containers/FrozenAction/actions';
import { getFrozenEntityId } from 'containers/FrozenAction/selectors';
import { addCarouselSlide, deleteCarouselSlide } from 'containers/Layouts/actions';
import { CombinedLayoutsList, LayoutMap } from 'models';
import styles from './styles.module.scss';

type Props = {
  dataTestId?: string;
  carouselId: string;
  sectionId: string;
  slideId: string;
  currentSlideNumber: number;
  isSlideDeleteDisabled: boolean;
  layoutIds: Immutable.List<string>;
  slideLayoutsInSlide: CombinedLayoutsList;
};

const CarouselItemSlideActions: React.FC<Props> = ({
  dataTestId = 'slide-actions',
  carouselId,
  sectionId,
  slideId,
  currentSlideNumber,
  isSlideDeleteDisabled,
  slideLayoutsInSlide,
  layoutIds,
}) => {
  const dispatch = useDispatch();
  const frozenEntityId = useSelector(getFrozenEntityId);
  const isActionFrozen = frozenEntityId === slideId;
  const { connectDragSource } = useCarouselItemSlideDragSource({ sectionId, carouselId, slideId, slideNumber: currentSlideNumber });

  const [showActions, setShowActions] = useState(isActionFrozen);
  const [rightActionsExpanded, setRightActionsExpanded] = useState(false);

  useEffect(() => {
    setShowActions(isActionFrozen);
  }, [isActionFrozen]);

  const handleFrozenAction = (): void => {
    if (frozenEntityId === slideId) {
      dispatch(setFrozenEntityIdAction(null));
    } else {
      dispatch(setFrozenEntityIdAction(slideId));
    }
  };

  const handleShowActions = (): void => {
    setShowActions(true);
  };

  const handleHideActions = (): void => {
    setRightActionsExpanded(false);
    if (isActionFrozen) {
      return;
    }
    setShowActions(false);
  };

  const handleMouseEnterOnRightActionMenu = (): void => {
    setRightActionsExpanded(true);
    handleShowActions();
  };

  const firstLayout = slideLayoutsInSlide.first();
  const lastLayout = slideLayoutsInSlide.last();
  const firstLayoutPosition = layoutIds.findIndex(id => (firstLayout as LayoutMap).get('id') === id);
  const lastLayoutPosition = layoutIds.findIndex(id => (lastLayout as LayoutMap).get('id') === id);

  const handleAddSlide = (newSlideNumber: number, position: number): void => {
    batch(() => {
      dispatch(updateDragHotspotPosition(position));
      dispatch(addCarouselSlide(carouselId, sectionId, newSlideNumber));
    });
  };

  const handleDeleteSlide = (): void => {
    dispatch(deleteCarouselSlide(carouselId, sectionId, slideId, currentSlideNumber));
  };

  const renderBorder = (): JSX.Element => (
    <AccessibleDiv
      onClick={handleFrozenAction}
      className={styles.border}
      onMouseEnter={handleShowActions}
      onMouseLeave={handleHideActions}
    />
  );

  const actionsClassNames = classNames(styles.SlideActions, {
    [styles.showActions]: showActions,
    [styles.frozen]: isActionFrozen,
  });

  const actionsLeftDataTestId = `${dataTestId}-actionsLeft`;
  const actionsRightDataTestId = `${dataTestId}-actionsRight`;
  const actionsBottomDataTestId = `${dataTestId}-actionsBottom`;
  const actionsTopDataTestId = `${dataTestId}-actionsTop`;

  return (
    <div className={actionsClassNames}>
      <div className={styles.left} data-testid={actionsLeftDataTestId}>
        {renderBorder()}
        <div
          ref={connectDragSource}
          className={classNames(styles.actions)}
          onMouseEnter={handleShowActions}
          onMouseLeave={handleHideActions}
        >
          <ActionIcon
            backgroundColor={ActionsColor.PURPLE}
            color="secondary"
            isDisabled={false}
            size="badge"
            type={IconType.UP_AND_DOWN_ARROWS}
            dataTestId={`${actionsLeftDataTestId}-actionIcon${IconType.UP_AND_DOWN_ARROWS}`}
          />
        </div>
      </div>
      <div className={styles.right} data-testid={actionsRightDataTestId}>
        {renderBorder()}
        {!isSlideDeleteDisabled && (
          <div
            className={classNames(styles.actions)}
            onMouseEnter={handleMouseEnterOnRightActionMenu}
            onMouseLeave={handleHideActions}
          >
            {rightActionsExpanded ? (
              <ActionIcon
                backgroundColor={ActionsColor.PURPLE}
                color="secondary"
                isDisabled={false}
                size="xs"
                type={IconType.DELETE}
                onClick={handleDeleteSlide}
                dataTestId={`${actionsRightDataTestId}-actionIcon${IconType.DELETE}`}
              />
            ) : (
              <ActionIcon
                backgroundColor={ActionsColor.PURPLE}
                color="secondary"
                size="xs"
                type={IconType.MENU}
                dataTestId={`${actionsRightDataTestId}-actionIcon${IconType.MENU}`}
              />
            )}
          </div>
        )}
      </div>
      <div className={styles.top} data-testid={actionsTopDataTestId}>
        <ActionIcon
          backgroundColor={ActionsColor.PURPLE}
          color="secondary"
          onMouseEnter={handleShowActions}
          onMouseLeave={handleHideActions}
          onClick={(): void => handleAddSlide(currentSlideNumber, firstLayoutPosition)}
          size="xs"
          type={IconType.ADD}
          dataTestId={`${actionsTopDataTestId}-actionIcon${IconType.ADD}`}
        />
      </div>
      <div className={styles.bottom} data-testid={actionsBottomDataTestId}>
        <ActionIcon
          backgroundColor={ActionsColor.PURPLE}
          color="secondary"
          onMouseEnter={handleShowActions}
          onMouseLeave={handleHideActions}
          onClick={(): void => handleAddSlide(currentSlideNumber + 1, lastLayoutPosition + 1)}
          size="xs"
          type={IconType.ADD}
          dataTestId={`${actionsBottomDataTestId}-actionIcon${IconType.ADD}`}
        />
      </div>
    </div>
  );
};

export default CarouselItemSlideActions;
